import {
  Editor,
  RecordsDiff,
  reverseRecordsDiff,
  structuredClone,
  TLRecord,
} from "tldraw";
import { AgentAction } from "../../shared/types/AgentAction";
import { AgentInput } from "../../shared/types/AgentInput";
import { AgentPrompt, BaseAgentPrompt } from "../../shared/types/AgentPrompt";
import { AgentRequest } from "../../shared/types/AgentRequest";
import {
  ChatHistoryItem,
  ChatHistoryPromptItem,
} from "../../shared/types/ChatHistoryItem";
import { PromptPart } from "../../shared/types/PromptPart";
import { Streaming } from "../../shared/types/Streaming";
import { WebSocketTransport, type AgentTransport } from "./transport";
import { AgentHelpers } from "../AgentHelpers";
import { commitInkChanges } from "../ink/inkChanges";
import { debugLog, debugWarn } from "@/utils/debugLog";
import { useTurnStore } from "@/features/tutor/store/useTurnStore";
import {
  CanvasChunkType,
  type CanvasInjectChunkData,
  type CanvasPageCaptureChunkData,
} from "./chunk_protocol";
import { sendSharedCanvasChunk } from "./shared-ws";
import { getModeNode } from "../modes/AgentModeChart";
import { AgentModeType } from "../modes/AgentModeDefinitions";
import {
  getPromptPartUtilsRecord,
  PromptPartUtil,
} from "../parts/PromptPartUtil";
import {
  extractCursorPositionFromAction,
  getAgentCursorStateFromAction,
} from "../components/cursor/extractCursorPosition";
import { AgentActionManager } from "./managers/AgentActionManager";
import { AgentChatManager } from "./managers/AgentChatManager";
import { AgentCursorManager } from "./managers/AgentCursorManager";
import {
  AgentDebugFlags,
  AgentDebugManager,
} from "./managers/AgentDebugManager";
import { AgentGestureManager } from "./managers/AgentGestureManager";
import { AgentCreatedShapesManager } from "./managers/AgentCreatedShapesManager";
import { AgentModeManager } from "./managers/AgentModeManager";
import { AgentRequestManager } from "./managers/AgentRequestManager";
import { AgentViewportFollowManager } from "./managers/AgentViewportFollowManager";
import { showImageFailureNotification } from "@/utils/imageUploadNotifications";

/**
 * The persisted state of an agent.
 * Used for saving and loading agent state.
 */
export interface PersistedAgentState {
  chatHistory?: ChatHistoryItem[];
  debugFlags?: AgentDebugFlags;
}

export interface TldrawAgentOptions {
  /** The editor to associate the agent with. */
  editor: Editor;
  /** A key used to differentiate the agent from other agents. */
  id: string;
  /** A callback for when an error occurs. */
  onError: (e: any) => void;
  /** Optional pre-created transport. Falls back to WebSocketTransport if not provided. */
  transport?: AgentTransport;
  /** Called when an LLM stream completes successfully. */
  onStreamComplete?: () => void;
  /** Called after each action is executed on the canvas. */
  onActionExecuted?: (action: Streaming<AgentAction>) => void;
  /** Called when a malformed action crashes the editor and diff rollback also fails. */
  onActionCrash?: () => void;
}

/**
 * An agent that can be prompted to edit the canvas.
 * Access the agent via `useAgent()` hook from TldrawAgentAppProvider,
 * or via `AgentAppAgentsManager.getAgent(editor)`.
 */
export class TldrawAgent {
  /** The editor associated with this agent. */
  editor: Editor;

  /** An id to differentiate the agent from other agents. */
  id: string;

  /** A callback for when an error occurs. */
  onError: (e: any) => void;

  // ==================== Managers ====================

  /** The action manager associated with this agent. */
  actions: AgentActionManager;

  /** The chat manager associated with this agent. */
  chat: AgentChatManager;

  /** The debug manager associated with this agent. */
  debug: AgentDebugManager;

  /** Tracks the shapes the agent created during the current prompt chain. */
  createdShapes: AgentCreatedShapesManager;

  /** The mode manager associated with this agent. */
  mode: AgentModeManager;

  /** The request manager associated with this agent. */
  requests: AgentRequestManager;

  /** The cursor manager associated with this agent. */
  cursor: AgentCursorManager;

  /**
   * Transient pointer marks the tutor makes while talking. Overlay-only —
   * nothing it holds ever reaches the tldraw store. See AgentGestureManager.
   */
  gestures: AgentGestureManager;

  /** The viewport follow manager associated with this agent. */
  viewportFollow: AgentViewportFollowManager;

  /** The transport used for agent communication. */
  private transport: AgentTransport;

  /** Called when an LLM stream completes successfully. */
  onStreamComplete?: () => void;

  /** Called after each action is executed on the canvas. */
  onActionExecuted?: (action: Streaming<AgentAction>) => void;

  /** Called when a malformed action crashes the editor and diff rollback also fails. */
  private onActionCrash?: () => void;

  /** Whether the last cancel was an explicit user cancel (not a supersede). */
  private _wasExplicitCancel = false;

  /**
   * Image messages staged by the user (e.g. via photo upload) that should be
   * prepended to the next agent turn rather than firing a turn of their own.
   */
  private _pendingImageMessages: string[] = [];

  /**
   * Stage image messages to be prepended to the next agent turn.
   * Does not trigger a turn itself — the images will be included when the
   * user next speaks or sends a message.
   */
  queueImageMessages(messages: string[]): void {
    this._pendingImageMessages.push(...messages);
  }

  dequeueImageMessage(dataUrl: string): void {
    const idx = this._pendingImageMessages.indexOf(dataUrl);
    if (idx !== -1) this._pendingImageMessages.splice(idx, 1);
    for (const [id, capture] of this._pageCaptures) {
      if (capture.dataUrl === dataUrl) this._pageCaptures.delete(id);
    }
  }

  /** Photos sent via CANVAS_PAGE_CAPTURE and still staged, by captureId. */
  private _pageCaptures = new Map<
    string,
    { dataUrl: string; digitized: boolean }
  >();

  /**
   * Ship a confirmed paper photo for out-of-turn OCR. Returns false when the
   * socket is closed; the staged copy is then digitized in-turn instead.
   */
  submitPageCapture(dataUrl: string, captureId: string): boolean {
    const ok = this.sendPageCapture(dataUrl, captureId, false);
    if (ok) this._pageCaptures.set(captureId, { dataUrl, digitized: false });
    return ok;
  }

  private sendPageCapture(
    dataUrl: string,
    captureId: string,
    digitized: boolean,
  ): boolean {
    const helpers = new AgentHelpers(this);
    const payload: CanvasPageCaptureChunkData = {
      png: dataUrl,
      captureId,
      // Before the first turn there is no `agentViewportBounds` part to read the panel size from.
      agentViewport: helpers.roundBox(this.editor.getViewportPageBounds()),
      ...(digitized ? { digitized: true } : {}),
    };
    const turnId = useTurnStore.getState().getTurnId() ?? "";
    const ok = sendSharedCanvasChunk(
      CanvasChunkType.CANVAS_PAGE_CAPTURE,
      JSON.stringify(payload),
      turnId,
    );
    if (ok) {
      debugLog("[TldrawAgent] CANVAS_PAGE_CAPTURE", {
        captureId,
        digitized,
        encodedKb: Math.round(dataUrl.length / 1024),
      });
    } else {
      debugWarn("[TldrawAgent] CANVAS_PAGE_CAPTURE skipped — WS not open");
    }
    return ok;
  }

  /**
   * A new gateway session has no record of what the old one digitized; re-claim
   * those photos so its in-turn path doesn't OCR and inject them a second time.
   */
  resendDigitizedPageCaptures(): void {
    for (const [captureId, { dataUrl, digitized }] of this._pageCaptures) {
      if (digitized) this.sendPageCapture(dataUrl, captureId, true);
    }
  }

  private _handleWsReconnected = () => this.resendDigitizedPageCaptures();

  markPageCaptureDigitized(captureId: string): void {
    const capture = this._pageCaptures.get(captureId);
    if (capture) capture.digitized = true;
  }

  // ==================== Prompt Part Utils ====================

  /**
   * A record of the agent's prompt part util instances.
   * Used by the `getPromptPartUtil` method.
   */
  promptPartUtils: Record<PromptPart["type"], PromptPartUtil<PromptPart>>;

  /**
   * Get a prompt part util for a specific part type.
   *
   * @param type - The type of part to get the util for.
   * @returns The part util.
   */
  getPromptPartUtil(type: PromptPart["type"]) {
    return this.promptPartUtils[type];
  }

  /**
   * Create a new tldraw agent.
   */
  constructor({
    editor,
    id,
    onError,
    transport,
    onStreamComplete,
    onActionExecuted,
    onActionCrash,
  }: TldrawAgentOptions) {
    this.editor = editor;
    this.id = id;
    this.onError = onError;
    this.onStreamComplete = onStreamComplete;
    this.onActionExecuted = onActionExecuted;
    this.onActionCrash = onActionCrash;

    // Initialize managers
    // Note: mode must be initialized before actions, since actions depends on mode
    this.mode = new AgentModeManager(this);
    this.actions = new AgentActionManager(this);
    this.chat = new AgentChatManager(this);
    this.cursor = new AgentCursorManager(this);
    this.debug = new AgentDebugManager(this);
    this.gestures = new AgentGestureManager(this);
    this.createdShapes = new AgentCreatedShapesManager(this);
    this.requests = new AgentRequestManager(this);
    this.viewportFollow = new AgentViewportFollowManager(this);
    this.transport = transport ?? new WebSocketTransport();

    // Note: Agent registration is handled by AgentAppAgentsManager.createAgent()

    // Initialize prompt part utils
    this.promptPartUtils = getPromptPartUtilsRecord(this);

    window.addEventListener("canvas:inject-actions", this._handleInjectActions);
    window.addEventListener("canvas:ws-reconnected", this._handleWsReconnected);

    // Swap the latest prompt's image data URLs for the gateway's transcriptions,
    // so chat history (and its persisted snapshot) does not hold image bytes.
    window.addEventListener(
      "canvas:image-transcriptions",
      this._handleImageTranscriptions,
    );
  }

  private _handleInjectActions = (event: Event) => {
    const detail = (event as CustomEvent<{ chunk_data?: string }>).detail;
    if (detail && typeof detail.chunk_data === "string") {
      this.applyInjectChunk(detail.chunk_data);
    }
  };

  /** Apply out-of-turn paper OCR (CANVAS_INJECT), whatever the turn state. */
  applyInjectChunk(chunkData: string): void {
    let payload: Partial<CanvasInjectChunkData> | null;
    try {
      payload = JSON.parse(chunkData);
    } catch {
      debugWarn("[TldrawAgent] CANVAS_INJECT parse failed");
      return;
    }
    const actions = (
      Array.isArray(payload?.actions) ? payload.actions : []
    ) as Streaming<AgentAction>[];
    // A `message` outside any turn would land in chat history unprompted.
    this.applySeedActions(actions.filter((a) => a._type !== "message"));
  }

  private _handleImageTranscriptions = (event: Event) => {
    const detail = (
      event as CustomEvent<{ turn_id: string; chunk_data: string }>
    ).detail;
    if (!detail) return;
    try {
      const payload = JSON.parse(detail.chunk_data) as {
        image_replacements?: (
          | string
          | { failed: "quality" | "error" }
          | null
        )[];
      };
      const replacements = payload.image_replacements;
      if (!Array.isArray(replacements)) return;

      this.chat.update((history) => {
        for (let i = history.length - 1; i >= 0; i -= 1) {
          const item = history[i];
          if (item.type !== "prompt") continue;
          const original = item.agentMessages ?? [];
          const imageCount = original.filter((m) =>
            m.startsWith("data:image/"),
          ).length;
          if (imageCount === 0) continue;
          const next: string[] = [];
          let imgIdx = 0;
          for (const entry of original) {
            if (!entry.startsWith("data:image/")) {
              next.push(entry);
              continue;
            }
            const replacement = replacements[imgIdx];
            imgIdx += 1;
            if (
              replacement === null ||
              replacement === undefined ||
              (typeof replacement === "object" && "failed" in replacement)
            ) {
              continue;
            }
            next.push(replacement as string);
          }
          const patched: ChatHistoryPromptItem = {
            ...item,
            agentMessages: next,
            agentFacingMessage: next.join("\n"),
          };
          const clone = history.slice();
          clone[i] = patched;
          return clone;
        }
        return history;
      });

      const qualityCount = replacements.filter(
        (entry) =>
          entry !== null &&
          typeof entry === "object" &&
          (entry as { failed: string }).failed === "quality",
      ).length;
      const errorCount = replacements.filter(
        (entry) =>
          entry !== null &&
          typeof entry === "object" &&
          (entry as { failed: string }).failed === "error",
      ).length;

      showImageFailureNotification(qualityCount, errorCount);
    } catch {
      /* non-fatal */
    }
  };

  // ==================== State Persistence ====================

  /**
   * Serialize the agent's state to a plain object for persistence.
   * This is called by the app-level persistence manager to save agent state.
   */
  serializeState(): PersistedAgentState {
    return {
      chatHistory: this.chat.getHistory(),
      debugFlags: this.debug.getDebugFlags(),
    };
  }

  /**
   * Load previously persisted state into the agent.
   * This is called by the app-level persistence manager to restore agent state.
   *
   * @param state - The persisted state to load.
   */
  loadState(state: PersistedAgentState) {
    if (state.chatHistory) {
      this.chat.setHistory(state.chatHistory);
    }
    if (state.debugFlags) {
      this.debug.setDebugFlags(state.debugFlags);
    }
  }

  /**
   * Dispose of the agent by cancelling requests and stopping listeners.
   */
  dispose() {
    window.removeEventListener(
      "canvas:image-transcriptions",
      this._handleImageTranscriptions,
    );
    window.removeEventListener(
      "canvas:inject-actions",
      this._handleInjectActions,
    );
    window.removeEventListener(
      "canvas:ws-reconnected",
      this._handleWsReconnected,
    );
    this.cancel();
    this.transport.dispose();

    // Dispose all managers
    this.actions.dispose();
    this.chat.dispose();
    this.cursor.dispose();
    this.debug.dispose();
    this.gestures.dispose();
    this.createdShapes.dispose();
    this.mode.dispose();
    this.requests.dispose();
    this.viewportFollow.dispose();

    // Note: Agent removal from registry is handled by AgentAppAgentsManager.deleteAgent()
  }

  /**
   * Whether the agent is currently acting on the editor or not.
   * This flag is used to prevent agent actions from being recorded as user actions.
   *
   * Do not use this to check if the agent is currently working on a request. Use `requests.getActiveRequest()` instead.
   */
  private isActingOnEditor = false;

  /**
   * Get whether the agent is currently acting on the editor.
   * @returns true if the agent is currently acting, false otherwise.
   */
  getIsActingOnEditor(): boolean {
    return this.isActingOnEditor;
  }

  /**
   * Set whether the agent is currently acting on the editor.
   * @param value - true if the agent is acting, false otherwise.
   */
  setIsActingOnEditor(value: boolean): void {
    this.isActingOnEditor = value;
  }

  // ==================== Request Handling ====================

  /**
   * Get a full prompt based on a request.
   *
   * @param request - The request to use for the prompt.
   * @param helpers - The helpers to use.
   * @returns The fully assembled prompt.
   */
  async preparePrompt(
    request: AgentRequest,
    helpers: AgentHelpers,
  ): Promise<AgentPrompt> {
    const { promptPartUtils } = this;
    const transformedParts: PromptPart[] = [];

    // Get available prompt part types from the current mode
    const modeDefinition = this.mode.getCurrentModeDefinition();
    if (!modeDefinition.active) {
      throw new Error(
        `Fairy is not in an active mode so can't act right now. Current mode: ${modeDefinition.type}`,
      );
    }

    const availablePromptPartTypes = modeDefinition.parts;

    for (const promptPartType of availablePromptPartTypes) {
      const util = promptPartUtils[promptPartType];
      if (!util)
        throw new Error(
          `Prompt part util not found for part type: ${promptPartType}`,
        );
      const part = await util.getPart(structuredClone(request), helpers);
      if (!part) continue;
      transformedParts.push(part);
    }

    return Object.fromEntries(
      transformedParts.map((part) => [part.type, part]),
    ) as AgentPrompt;
  }

  /**
   * Send a request to the agent and apply its streamed actions.
   * Supersedes any request still in flight.
   *
   * @param input - The input to form the request from.
   * @returns A promise for when the request is complete.
   */
  async request(input: AgentInput) {
    const request = this.requests.getFullRequestFromInput(input);

    // Interrupt any currently active request (supersede, not explicit cancel)
    if (this.requests.getActiveRequest() !== null) {
      this.cancel();
      this._wasExplicitCancel = false; // override: this is a supersede, not explicit cancel
    }
    this.requests.setActiveRequest(request);

    // Call an external helper function to request the agent
    const { promise, cancel } = this.requestAgentActions(request);

    this.requests.setCancelFn(cancel);

    const results = await promise;
    this.requests.clearActiveRequest();

    return results;
  }

  /**
   * Interrupt the agent and optionally set its mode.
   */
  interrupt({ mode }: { mode?: AgentModeType } = {}) {
    this.requests.cancel();
    if (mode) {
      this.mode.setMode(mode);
    }
  }

  // ==================== Cancel & Reset ====================

  /**
   * Cancel the agent's current prompt, if one is active.
   */
  cancel() {
    this._wasExplicitCancel = true;
    const activeRequest = this.requests.getActiveRequest();

    if (activeRequest) {
      const modeType = this.mode.getCurrentModeType();
      const modeNode = getModeNode(modeType);
      modeNode.onPromptCancel?.(this, activeRequest);

      const newModeDefinition = this.mode.getCurrentModeDefinition();
      if (newModeDefinition.active) {
        throw new Error(
          `Agent is not allowed to become inactive during the active mode: ${this.mode.getCurrentModeType()}`,
        );
      }
    }

    this.requests.cancel();
  }

  /**
   * Reset the agent's chat and memory.
   * Cancel the current request if there's one active.
   */
  reset() {
    this.cancel();

    // Reset all managers
    this.actions.reset();
    this.chat.reset();
    this.cursor.reset();
    this.gestures.reset();
    this.createdShapes.reset();
    this.mode.reset();
    this.requests.reset();
    this.viewportFollow.reset();
  }

  // ==================== Request Helpers ====================

  /**
   * Send a request to the agent and handle its response.
   *
   * This is a helper function that is used internally by the agent.
   */
  private requestAgentActions(request: AgentRequest) {
    const { editor } = this;

    // Reset so a previous cancel's flag cannot leak into this request.
    this._wasExplicitCancel = false;

    // Prepend any staged image messages (placed before this turn fired).
    // Source "user": images are the student's even on a self-sourced voice turn.
    if (this._pendingImageMessages.length > 0) {
      request = {
        ...request,
        source: "user",
        agentMessages: [
          ...this._pendingImageMessages,
          ...request.agentMessages,
        ],
      };
      this._pendingImageMessages = [];
      this._pageCaptures.clear();
    }

    const agentFacingMessage = request.agentMessages.join("\n");
    const userFacingMessage =
      request.userMessages.length > 0 ? request.userMessages.join("\n") : null;
    const hasPromptText =
      agentFacingMessage.trim().length > 0 ||
      (userFacingMessage?.trim().length ?? 0) > 0;

    // Skip adding blank prompt bubbles (used by voice-bridge stream setup).
    const promptHistoryItem: ChatHistoryPromptItem | null = hasPromptText
      ? {
          type: "prompt",
          promptSource: request.source,
          agentFacingMessage,
          agentMessages: request.agentMessages,
          userFacingMessage,
        }
      : null;
    if (promptHistoryItem) {
      this.chat.push(promptHistoryItem);
    }

    let cancelled = false;
    const controller = new AbortController();
    const signal = controller.signal;
    const helpers = new AgentHelpers(this);

    const modeDefinition = this.mode.getCurrentModeDefinition();
    if (!modeDefinition.active) {
      this.cancel();
      throw new Error(
        `Agent is not in an active mode so cannot take actions. Current mode: ${modeDefinition.type}`,
      );
    }

    const availableActions = modeDefinition.actions;

    const requestPromise = (async () => {
      const prompt = await this.preparePrompt(request, helpers);
      // Screenshot preparation can outlive canvas cleanup or a superseding
      // turn. Do not touch disposed managers or open a stream after cancellation.
      if (signal.aborted) return;
      const actionPromises: Promise<void>[] = [];
      let hasAppliedActions = false;
      this.viewportFollow.onRequestStart();
      // Don't override "listening" — the student is still recording.
      // CanvasVoiceBridge will switch to "thinking" on USER_AUDIO_DONE.
      if (this.cursor.getState() !== "listening") {
        this.cursor.showThinking();
      }
      // The ink baseline advances only once something comes back, which is the
      // proof the prompt was not dropped as abandoned (see transport's
      // `signal.aborted` guard). A turn that yields nothing leaves the writing
      // flagged for next turn, which is the safe direction.
      let inkCommitted = false;
      try {
        for await (const action of this.streamAgentActions({
          prompt,
          signal,
        })) {
          if (cancelled) break;
          if (!inkCommitted) {
            commitInkChanges(this);
            helpers.acknowledgeStudentEdits();
            inkCommitted = true;
          }

          // Set acting flag BEFORE editor.run so user action tracker ignores all changes
          // including diff reverts that happen before act() is called
          this.setIsActingOnEditor(true);
          let lastActionDiff: RecordsDiff<TLRecord> | null = null;
          try {
            editor.run(
              () => {
                const actionUtilType = this.actions.getAgentActionUtilType(
                  action._type,
                );
                const actionUtil = this.actions.getAgentActionUtil(
                  action._type,
                );

                // If the action is not in the mode's available actions, skip it
                if (!availableActions.includes(actionUtilType)) {
                  return;
                }

                // Sanitize the agent's action
                const transformedAction = actionUtil.sanitizeAction(
                  action,
                  helpers,
                );
                if (!transformedAction) {
                  return;
                }

                // Apply the action to the app and editor
                const { diff, promise } = this.actions.act(
                  transformedAction,
                  helpers,
                );
                lastActionDiff = diff;
                hasAppliedActions = true;

                if (promise) {
                  actionPromises.push(promise);
                }

                this.createdShapes.trackShapesFromDiff(diff);

                // Notify parent about each executed action
                this.onActionExecuted?.(action);

                // Update cursor position and state
                const newPos = extractCursorPositionFromAction(action, editor);
                if (newPos) this.cursor.setPosition(newPos);
                this.viewportFollow.onActionPosition(newPos);
                this.cursor.setState(getAgentCursorStateFromAction(action));

                this.debug.logCompletedAction(transformedAction);
              },
              {
                ignoreShapeLock: true,
                history: "ignore",
              },
            );
          } catch (e) {
            debugWarn(
              "[TldrawAgent] editor.run threw. Action:",
              JSON.stringify(action, null, 2),
              "Error:",
              e,
            );
            // Roll back the malformed shape so it doesn't poison the store/snapshot
            if (lastActionDiff) {
              try {
                editor.store.applyDiff(reverseRecordsDiff(lastActionDiff));
              } catch {
                debugWarn(
                  "[TldrawAgent] Diff rollback failed, delegating to onActionCrash",
                );
                this.onActionCrash?.();
              }
              lastActionDiff = null;
            }
          } finally {
            this.setIsActingOnEditor(false);
          }
        }
        await Promise.all(actionPromises);

        // Notify parent when stream completes successfully (not on cancel/error)
        if (!cancelled) {
          this.onStreamComplete?.();
          this.cursor.hide();
          // Gestures are deliberately NOT cleared here. The transport holds
          // `end` until its timed queue drains, so the turn's last gesture
          // lands in the same tick as this — clearing would delete it in the
          // frame it appeared. Each one is bounded by its own timer, and
          // barge-in and error below still hard-clear.
          this.viewportFollow.onRequestEnd();
        }
      } catch (e) {
        this.cursor.hide();
        // The audio these were timed against is gone — a mark still pointing
        // at a word nobody is saying is worse than no mark.
        this.gestures.clear();
        this.viewportFollow.onRequestEnd();
        this.onError(e);
        return;
      }

      // Abort ends the stream cleanly, so cancel cleanup runs here. Match the
      // prompt by identity: rapid supersedes may have pushed newer prompts.
      if (cancelled) {
        this.cursor.hide();
        // Barge-in: the student is talking over the tutor. Drop the marks with
        // the speech that motivated them, mid-animation if need be.
        this.gestures.clear();
        this.viewportFollow.onRequestEnd();
      }
      if (cancelled && promptHistoryItem) {
        if (this._wasExplicitCancel && !hasAppliedActions) {
          // No output was drawn — remove the prompt from history entirely
          this.chat.update((history) => {
            const idx = history.indexOf(promptHistoryItem);
            if (idx !== -1) {
              return [...history.slice(0, idx), ...history.slice(idx + 1)];
            }
            return history;
          });
        }
        this._wasExplicitCancel = false;
      }
    })();

    const cancel = () => {
      cancelled = true;
      controller.abort("Cancelled by user");
    };

    return { promise: requestPromise, cancel };
  }

  /**
   * Apply gateway-seeded actions (CANVAS_SEED, CANVAS_INJECT) outside a turn.
   *
   * Runs each action through the same sanitize + act pipeline as streamed
   * actions, so canvas application and chat history recording behave exactly
   * as if the agent drew these shapes itself during a turn. Used to place a
   * notepad bite's opening content before the opening "start teaching" call.
   *
   * Runs with `isActingOnEditor` set for the whole batch. That flag is what
   * registerStudentShapeProtection (utils/studentShapeProtection.ts) uses to
   * distinguish agent mutations from student work:
   * without it the beforeCreate handler reads the seed as a student
   * duplicating a fragment and STRIPS `meta.source`, disarming the guard that
   * keeps the practice problem un-deletable (ENGG-2438).
   */
  applySeedActions(actions: Streaming<AgentAction>[]): void {
    const helpers = new AgentHelpers(this);
    this.setIsActingOnEditor(true);
    try {
      for (const action of actions) {
        let lastActionDiff: RecordsDiff<TLRecord> | null = null;
        try {
          this.editor.run(
            () => {
              const actionUtil = this.actions.getAgentActionUtil(action._type);
              const transformed = actionUtil.sanitizeAction(action, helpers);
              if (!transformed) return;
              const { diff, failed } = this.actions.act(transformed, helpers);
              lastActionDiff = diff;
              // Parity with the streamed-action pipeline: seeded shapes are
              // tracked as created shapes like turn-drawn shapes.
              if (!failed) {
                this.createdShapes.trackShapesFromDiff(diff);
              }
            },
            {
              ignoreShapeLock: true,
              history: "ignore",
            },
          );
        } catch (e) {
          debugWarn(
            "[TldrawAgent] Seed action failed, skipping:",
            JSON.stringify(action, null, 2),
            "Error:",
            e,
          );
          // Roll back the malformed shape so it doesn't poison the store or the
          // persisted snapshot — same recovery as the streamed-action pipeline.
          if (lastActionDiff) {
            try {
              this.editor.store.applyDiff(reverseRecordsDiff(lastActionDiff));
            } catch {
              debugWarn("[TldrawAgent] Seed action diff rollback failed");
            }
          }
        }
      }
    } finally {
      this.setIsActingOnEditor(false);
    }
  }

  /**
   * Stream a response from the model.
   * Delegates to the active transport.
   *
   * This is a helper function that is used internally by the agent.
   */
  private async *streamAgentActions({
    prompt,
    signal,
  }: {
    prompt: BaseAgentPrompt;
    signal: AbortSignal;
  }): AsyncGenerator<Streaming<AgentAction>> {
    yield* this.transport.stream(prompt, signal);
  }
}
